import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Plus, Users, Flag, ArrowRight, Trash2, Link2, Unlink, X, Camera, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CoachPhotosTab from "@/components/coach/CoachPhotosTab";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import HeaderUtils from "@/components/HeaderUtils";
import { toast } from "sonner";

const field = "w-full h-11 bg-black/50 border border-white/15 px-3 text-sm text-white outline-none focus:border-[#E2E30A]";
const label = "text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold";

// Coach race area — build race week + race day plans for athletes. Race plans
// only: no weekly meal planning, no tracker.
export default function CoachRace() {
  const navigate = useNavigate();
  const { isCoach, loading: authLoading } = useAuth();
  const [athletes, setAthletes] = useState(null);
  const [plans, setPlans] = useState([]);
  const [adding, setAdding] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", weight: "", height: "", age: "", sex: "male" });
  const [photoAthlete, setPhotoAthlete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/coach-race/athletes"),
        api.get("/coach-race/race-plans"),
      ]);
      setAthletes(a.data || []);
      setPlans(p.data || []);
    } catch (e) {
      toast.error(apiError(e, "Could not load your roster."));
      setAthletes([]);
    }
  };

  useEffect(() => { if (isCoach) load(); }, [isCoach]);

  const addAthlete = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/coach-race/athletes", {
        name: form.name.trim(),
        email: form.email.trim() || null,
        profile: {
          weight: Number(form.weight) || null, height: Number(form.height) || null,
          age: Number(form.age) || null, sex: form.sex, activity: "active",
        },
      });
      toast.success("Athlete added.");
      setForm({ name: "", email: "", weight: "", height: "", age: "", sex: "male" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not add that athlete."));
    } finally {
      setSaving(false);
    }
  };

  const linkAthlete = async () => {
    if (!linkEmail.trim()) return;
    setLinking(true);
    try {
      await api.post("/coach-race/athletes/link", { email: linkEmail.trim() });
      toast.success("Athlete attached to your account.");
      setLinkEmail("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not attach that athlete."));
    } finally {
      setLinking(false);
    }
  };

  const openEdit = (a) => setEditing({
    id: a.id, name: a.name || "", email: a.email || "",
    weight: a.profile?.weight ?? "", height: a.profile?.height ?? "",
    age: a.profile?.age ?? "", sex: a.profile?.sex || "male",
  });

  const saveEdit = async () => {
    if (!editing?.name.trim()) return;
    setEditSaving(true);
    try {
      await api.put(`/coach-race/athletes/${encodeURIComponent(editing.id)}`, {
        name: editing.name.trim(),
        email: editing.email.trim() || null,
        profile: {
          weight: Number(editing.weight) || null, height: Number(editing.height) || null,
          age: Number(editing.age) || null, sex: editing.sex, activity: "active",
        },
      });
      toast.success("Athlete updated.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not update that athlete."));
    } finally {
      setEditSaving(false);
    }
  };

  const removeAthlete = async (id, name) => {
    const linked = id.startsWith("user:");
    const msg = linked
      ? `Unlink ${name} from your account? Their own account and plans stay as they are.`
      : `Remove ${name} and their race plans?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/coach-race/athletes/${encodeURIComponent(id)}`);
      toast.success(linked ? "Athlete unlinked." : "Athlete removed.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not update your roster."));
    }
  };

  if (authLoading || (isCoach && athletes === null)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
      </div>
    );
  }

  if (!isCoach) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center p-6">
        <div className="text-center max-w-sm" data-testid="coach-no-access">
          <p className="font-display text-2xl uppercase tracking-tight">Coach access only</p>
          <p className="text-sm text-zinc-400 mt-2">
            This area is for coaches building race plans for athletes. Email{" "}
            <a href="mailto:info@smartperformancecoaching.co.uk" className="text-[#E2E30A] underline">
              info@smartperformancecoaching.co.uk
            </a>{" "}
            to get set up.
          </p>
          <Link to="/" className="mt-6 inline-block bg-[#E2E30A] text-black font-display font-black uppercase tracking-wider px-5 py-3">
            Back to my plan
          </Link>
        </div>
      </div>
    );
  }

  const plansFor = (id) => plans.filter((p) => p.athleteId === id);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-20" data-testid="coach-race-page">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/coach" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <span className="font-display text-base uppercase tracking-tight">
              <span className="text-[#E2E30A]">Coach</span> Race Plans
            </span>
          </Link>
          <HeaderUtils />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Coach area</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter font-black mt-2">
          Your athletes
        </h1>
        <p className="text-sm text-zinc-300 mt-3 max-w-xl">
          Build a race week + race day fuelling plan for any athlete. Add them with a few
          numbers — no account needed. Athletes linked to your coach account see their plan in
          their own app too.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <div className="border border-white/12 bg-white/[0.02] p-4">
            <p className={label}>Attach an existing SPC athlete</p>
            <p className="text-xs text-zinc-400 mt-1">
              They already have an account — enter the email they signed up with.
            </p>
            <div className="flex gap-2 mt-3">
              <input data-testid="coach-link-email" className={field} value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)} placeholder="athlete@email.com" />
              <button type="button" data-testid="coach-link-btn" onClick={linkAthlete}
                disabled={linking || !linkEmail.trim()}
                className="inline-flex items-center gap-2 shrink-0 border border-[#E2E30A]/60 hover:bg-[#E2E30A] hover:text-black text-[#E2E30A] px-4 text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-40">
                {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Attach
              </button>
            </div>
          </div>
          <div className="border border-white/12 bg-white/[0.02] p-4">
            <p className={label}>Or add a standalone athlete</p>
            <p className="text-xs text-zinc-400 mt-1">
              No SPC account needed — just the numbers a race plan runs on.
            </p>
            <button type="button" data-testid="coach-add-athlete-btn" onClick={() => setAdding(!adding)}
              className="mt-3 inline-flex items-center gap-2 bg-[#E2E30A] hover:bg-white text-black font-display font-black uppercase tracking-wider px-5 py-3 transition-colors">
              {adding ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {adding ? "Cancel" : "Add athlete"}
            </button>
          </div>
        </div>

        {adding && (
          <div className="mt-4 border border-white/12 bg-white/[0.02] p-5" data-testid="coach-add-athlete-form">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <p className={label}>Athlete name</p>
                <input data-testid="coach-athlete-name" className={`${field} mt-1.5`} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
              </div>
              <div>
                <p className={label}>Email (optional)</p>
                <input data-testid="coach-athlete-email" className={`${field} mt-1.5`} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" />
              </div>
              <div>
                <p className={label}>Weight (kg)</p>
                <input data-testid="coach-athlete-weight" type="number" className={`${field} mt-1.5`} value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="62" />
              </div>
              <div>
                <p className={label}>Height (cm)</p>
                <input data-testid="coach-athlete-height" type="number" className={`${field} mt-1.5`} value={form.height}
                  onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="170" />
              </div>
              <div>
                <p className={label}>Age</p>
                <input data-testid="coach-athlete-age" type="number" className={`${field} mt-1.5`} value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="34" />
              </div>
              <div>
                <p className={label}>Sex</p>
                <select data-testid="coach-athlete-sex" className={`${field} mt-1.5`} value={form.sex}
                  onChange={(e) => setForm({ ...form, sex: e.target.value })}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <button type="button" data-testid="coach-save-athlete" onClick={addAthlete}
              disabled={saving || !form.name.trim()}
              className="mt-5 inline-flex items-center gap-2 bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-wider px-5 py-3 transition-colors">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save athlete
            </button>
          </div>
        )}

        {/* Roster */}
        <section className="mt-10">
          {!athletes.length ? (
            <div className="border border-dashed border-white/15 p-10 text-center" data-testid="coach-empty">
              <Users className="h-8 w-8 text-zinc-500 mx-auto mb-3" />
              <p className="font-display text-lg uppercase">No athletes yet</p>
              <p className="text-sm text-zinc-400 mt-2">Add your first athlete to build them a race plan.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {athletes.map((a) => (
                <div key={a.id} className="border border-white/12 bg-white/[0.02] p-4" data-testid={`coach-athlete-${a.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className="grid place-items-center h-10 w-10 shrink-0 bg-[#E2E30A]/10 border border-[#E2E30A]/40">
                      <Users className="h-4 w-4 text-[#E2E30A]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg uppercase tracking-tight flex flex-wrap items-center gap-2">
                        {a.name}
                        {a.linked && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#34C759] border border-[#34C759]/40 px-1.5 py-0.5">
                            <Link2 className="h-2.5 w-2.5" /> Linked account
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {a.profile?.weight ? `${a.profile.weight}kg` : "no weight set"}
                        {a.profile?.age ? ` · ${a.profile.age}y` : ""}
                        {a.email ? ` · ${a.email}` : ""}
                        {` · ${plansFor(a.id).length} race plan${plansFor(a.id).length === 1 ? "" : "s"}`}
                      </p>
                    </div>
                    <button type="button" data-testid={`coach-build-${a.id}`}
                      onClick={() => navigate(`/coach/athletes/${encodeURIComponent(a.id)}/new`)}
                      className="inline-flex items-center justify-center gap-2 bg-[#E2E30A] hover:bg-white text-black px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors w-full sm:w-auto">
                      <Flag className="h-3.5 w-3.5" /> Build race plan
                    </button>
                    {a.linked && a.email && (
                      <button type="button" data-testid={`coach-photos-${a.id}`}
                        onClick={() => setPhotoAthlete({ email: a.email, name: a.name })}
                        className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#FB7185]/60 text-white px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors w-full sm:w-auto">
                        <Camera className="h-3.5 w-3.5 text-[#FB7185]" /> Progress photos
                      </button>
                    )}
                    {!a.linked && (
                      <button type="button" data-testid={`coach-edit-athlete-${a.id}`}
                        onClick={() => openEdit(a)}
                        className="p-2 text-zinc-400 hover:text-[#E2E30A] transition-colors" title="Edit athlete details">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {!a.linked ? (
                      <button type="button" data-testid={`coach-remove-${a.id}`}
                        onClick={() => removeAthlete(a.id, a.name)}
                        className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors" title="Remove athlete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : (
                      <button type="button" data-testid={`coach-unlink-${a.id}`}
                        onClick={() => removeAthlete(a.id, a.name)}
                        className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors" title="Unlink athlete">
                        <Unlink className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {!!plansFor(a.id).length && (
                    <div className="mt-3 space-y-1.5 border-t border-white/8 pt-3">
                      {plansFor(a.id).map((p) => (
                        <div key={p.id} className="flex items-center gap-1 border border-white/10 hover:border-[#E2E30A]/50 hover:bg-[#E2E30A]/[0.04] transition-colors">
                          <button type="button" data-testid={`coach-plan-${p.id}`}
                            onClick={() => navigate(`/coach/plans/${p.id}`)}
                            className="flex-1 min-w-0 flex items-center gap-3 text-left px-3 py-2">
                            <Flag className="h-3.5 w-3.5 text-[#E2E30A] shrink-0" />
                            <span className="flex-1 min-w-0 text-[13px] text-white truncate">{p.name}</span>
                            <span className="text-[11px] text-zinc-400">{p.raceDate}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-zinc-500" />
                          </button>
                          <button type="button" data-testid={`coach-plan-edit-${p.id}`}
                            onClick={() => navigate(`/coach/plans/${p.id}/edit`)}
                            aria-label={`Edit ${p.name}`} title="Edit race plan"
                            className="shrink-0 grid place-items-center h-8 w-8 mr-1 text-zinc-400 hover:text-[#E2E30A] hover:bg-[#E2E30A]/10 transition-colors">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="coach-edit-athlete-dialog"
          className="force-dark bg-[#141414] border-white/15 text-white sm:max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="text-xs font-bold uppercase tracking-[0.22em] text-white">
              Edit athlete
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-400">
              These numbers drive every target in their race plans.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <p className={label}>Name</p>
                <input data-testid="coach-edit-name" className={`${field} mt-1.5`} value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <p className={label}>Email (optional)</p>
                <input data-testid="coach-edit-email" className={`${field} mt-1.5`} value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div>
                <p className={label}>Weight (kg)</p>
                <input data-testid="coach-edit-weight" type="number" className={`${field} mt-1.5`} value={editing.weight}
                  onChange={(e) => setEditing({ ...editing, weight: e.target.value })} />
              </div>
              <div>
                <p className={label}>Height (cm)</p>
                <input data-testid="coach-edit-height" type="number" className={`${field} mt-1.5`} value={editing.height}
                  onChange={(e) => setEditing({ ...editing, height: e.target.value })} />
              </div>
              <div>
                <p className={label}>Age</p>
                <input data-testid="coach-edit-age" type="number" className={`${field} mt-1.5`} value={editing.age}
                  onChange={(e) => setEditing({ ...editing, age: e.target.value })} />
              </div>
              <div>
                <p className={label}>Sex</p>
                <select data-testid="coach-edit-sex" className={`${field} mt-1.5`} value={editing.sex}
                  onChange={(e) => setEditing({ ...editing, sex: e.target.value })}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <button type="button" data-testid="coach-edit-save" onClick={saveEdit}
                disabled={editSaving || !editing.name.trim()}
                className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-wider px-5 py-3 transition-colors">
                {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoAthlete} onOpenChange={(o) => !o && setPhotoAthlete(null)}>
        <DialogContent data-testid="coach-photos-dialog"
          className="force-dark bg-[#141414] border-white/15 text-white sm:max-w-2xl rounded-none p-0 gap-0 max-h-[92vh] overflow-y-auto">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10">
            <DialogTitle className="text-xs font-bold uppercase tracking-[0.22em] text-white flex items-center gap-2">
              <Camera className="h-4 w-4 text-[#FB7185]" /> {photoAthlete?.name} · Progress photos
            </DialogTitle>
            <DialogDescription className="sr-only">Compare your athlete's progress photos.</DialogDescription>
          </DialogHeader>
          <div className="p-5">
            {photoAthlete && <CoachPhotosTab email={photoAthlete.email} name={photoAthlete.name} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
